<template>
<div class="card" :class="{ 'collapsed': collapsed, 'bg-transparent': loadError }" id="policyCard">

  <!-- LOAD ERROR -->
  <div v-if="loadError" class="alert small px-2 py-1 alert-danger">{{ loadError | translate('msg') }}</div>

  <template v-else-if="summary">
    <div class="card-header" id="policyCard-header">

      <!-- PRODUCT / INSURER -->
      <span :title="summary.productDescription || ''" :id="'policyCard-'+translatedProductInsurer">{{ translatedProductInsurer }}</span>

      <!-- VALIDATIONS -->
      <span class="val-icon" :class="validationsIconClass" id="policyCard-valIcon"
            v-tooltip="$options.filters.translate('SHOW_VALIDATIONS')"
            @click.stop="$emit('show-validations', { product: translatedProductInsurer, validations: summary.validations })">
        <i class="fa fa-exclamation" aria-hidden="true" id="policyCard-valIcon-diamond"></i>
      </span>

    </div>
    <div class="card-body" id="policyCard-cardBody">
      <div class="premium" id="policyCard-premiumAmount">

        <!-- PREMIUM DELTA (Preview) -->
        <span v-if="premiumDelta !== null" class="premium-delta mr-1" id="policyCard-premiumDelta">
          <span class="badge badge-secondary" v-if="premiumDelta === 0">{{ 'EQUAL' | translate }}</span>
          <span class="badge badge-secondary" v-else-if="premiumDelta === 'not-available'">{{ 'NOT_AVAILABLE' | translate }}</span>
          <span class="badge badge-danger" v-else-if="premiumDelta > 0">+ {{ premiumDelta | currency }}</span>
          <span class="badge badge-success" v-else-if="premiumDelta < 0">- {{ Math.abs(premiumDelta) | currency }}</span>
        </span>

        <!-- PREMIUM -->
        {{ summary.totalNetPremium || '-' | currency }}

        <!-- PAYMENT TERM -->
        <small v-if="summary.totalNetPremium">/ {{ summary.paymentTerm | translate('term') }}</small>

      </div>
      <table v-if="!collapsed" class="table table-borderless table-sm mt-3 mb-0">
        <caption class="accessibly-hidden">{{ 'SUMMARY' | translate }}</caption>

        <!-- COVERS -->
        <tr v-for="cover in summary.covers" :key="cover.type">
          <th scope="row">{{ cover.type | translate(translationKeyForCovers(cover.type)) }}</th>
          <td>{{ pricePerTerm(cover.value) | currency }}</td>
        </tr>

        <tr class="separator"><td></td></tr>

        <!-- OWN RISKS -->
        <tr v-for="ownRisk in sortedOwnRisks" :key="ownRisk.type">
          <th scope="row">{{ translationKeyForOwnrisks(ownRisk.type) }}</th>
          <td>{{ ownRisk.value | currency }}</td>
        </tr>

        <tr class="separator"><td></td></tr>

        <!-- INSURED AMOUNTS -->
        <tr v-for="insuredAmount in sortedInsuredAmounts" :key="insuredAmount.type">
          <th scope="row">{{ translationKeyForInsuredamounts(insuredAmount.type) }}</th>
          <td>{{ insuredAmount.value | currency }}</td>
        </tr>

        <tr class="separator"><td></td></tr>

        <!-- AUTO DEPRECATION SCHEME -->
        <tr v-if="summary.productLine === 'PASSENGERCAR' && summary.deprecationScheme">
          <th scope="row">{{ 'auto.depreciationScheme' | translate }}</th>
          <td>{{ summary.deprecationScheme | translate('auto.depreciationScheme') }}</td>
        </tr>

        <!-- TRAVEL MAX DURATION -->
        <tr v-if="summary.productLine === 'TRAVEL' && summary.maxTravelDuration">
          <th scope="row">{{ 'travel.maxTravelDuration' | translate }}</th>
          <td>{{ summary.maxTravelDuration }} {{ 'DAYS' | translate }}</td>
        </tr>

        <tr class="separator"><td></td></tr>
        <tr v-if=" summary.commissionAmount !== null && summary.commissionAmount.length !== 0">
           <th scope="row">{{ 'commissionAmount' | translate }}</th>
           <td>{{ summary.commissionAmount || '-' | currency }}</td>
        </tr>

      </table>

    </div>

    <!-- PREVIEW INDICATOR -->
    <div v-if="originalPremium" class="preview-indicator"><i class="fa fa-eye" aria-hidden="true"></i></div>

  </template>
</div>
</template>

<script>
import RestService from '../../service/RestService';
import GeneralConfigService from '../../service/GeneralConfigService';

export default {
  props: ['policyId', 'policySummary', 'collapsed', 'originalPremium'],
  data: function () {
    return {
      rest: this.initRestService(),
      summary: this.policySummary,
      loadError: null,
      validationsModalShown: false,
      products: []
    }
  },
  async beforeMount() {
    if (this.policyId) await this.loadSummary();
    if (this.products.length === 0) await this.getAllProducts();
  },
  mounted() {
    // We emit here only for preview summaries, regular summaries emit the same event later on when summary is loaded from the server.
    if (!this.policyId) this.$emit('summary-loaded', this.summary.productName);
  },
  computed: {
    translatedProductInsurer() {
      let product = this.products.find(product => product.name === this.summary.productName)
      return product ? product.description : '-';
    },
    sortedOwnRisks() {
      let output = Object.keys(this.summary.ownRisks).map(orType => ({ type: orType, value: this.summary.ownRisks[orType] }));
      return output.sort(this.objectValuesComparator);
    },
    sortedInsuredAmounts() {
      let output = Object.keys(this.summary.insuredAmounts).map(iaType => ({ type: iaType, value: this.summary.insuredAmounts[iaType] }));
      return output.sort(this.objectValuesComparator);
    },
    validationsIconClass() {
      if (typeof this.summary.validations === 'undefined' || this.summary.validations.length < 1) return 'd-none';
      else if (this.summary.validations.some(val => val.level === 'ERROR')) return 'bg-danger';
      else if (this.summary.validations.some(val => val.level === 'WARN')) return 'bg-warning';
      else if (this.summary.validations.some(val => val.level === 'INFO')) return 'bg-info';
      else if (this.summary.validations.some(val => val.level === 'AUDIT')) return 'bg-light text-dark';
      else return 'd-none';
    },
    premiumDelta() {
      if (!this.originalPremium) return null;
      if (!this.summary.totalNetPremium) return 'not-available';
      return this.summary.totalNetPremium - this.originalPremium;
    }
  },
  methods: {
    initRestService() {
      return new RestService();
    },
    async getAllProducts() {
      this.loadError = null;
      try {
        let response = await GeneralConfigService.getAllProductRows();
        this.products = response;
      } catch (error) {
        this.loadError = 'ERROR_LOADING_SUMMARY';
        this.$log.error(error.response.data);
      }
    },
    async loadSummary() {
      this.loadError = null;
      try {
        let response = await this.rest.getPolicySummary(this.policyId);
        this.summary = response.data;
        this.$emit('val-exclude-audit-updated', this.policyId, this.validationsOtherThanAuditExist());
        this.$emit('val-error-updated', this.policyId, this.errorsExist());
        this.$emit('summary-loaded', this.policyId);
      } catch (error) {
        this.loadError = 'ERROR_LOADING_SUMMARY';
        this.$log.error(error.response.data);
      }
    },
    objectValuesComparator(o1, o2) {
      if (o1.value < o2.value) return 1;
      if (o1.value > o2.value) return -1;
      return 0;
    },
    pricePerTerm(yearPrice) {
      switch (this.summary.paymentTerm) {
        case 'MONTH':
          return yearPrice / 12;
        case 'QUARTER':
          return yearPrice / 4;
        case 'HALF_YEAR':
          return yearPrice / 2;
        case 'YEAR':
          return yearPrice;
        default:
          return yearPrice;
      }
    },
    translationKeyForCovers(coverType) {
      const hasUnderscore = coverType.includes('_');
      if (hasUnderscore) {
        return (((this.summary.productName && this.summary.productName.includes('HOUSECOMBI')) ||
          (this.summary.productDescription && this.summary.productDescription.includes('Combi'))) &&
          (coverType.endsWith('_TOP') || coverType.endsWith('_BASIC') || coverType.endsWith('_GLASS')))
          ? 'combiCover' : 'cover';
      }
      return null;
    },
    translationKeyForInsuredamounts(iaType) {
      return (((this.summary.productName && this.summary.productName.includes('HOUSECOMBI')) ||
          (this.summary.productDescription && this.summary.productDescription.includes('Combi'))) &&
          (iaType.endsWith('_GENERIC')))
          ? 'combiInsuredamount' : 'Verzekerd bedrag';
    },
    translationKeyForOwnrisks(orType) {
      return (((this.summary.productName && this.summary.productName.includes('HOUSECOMBI')) ||
          (this.summary.productDescription && this.summary.productDescription.includes('Combi'))) &&
          (orType.endsWith('_GENERIC')))
          ? 'combiOwnrisk' : 'Eigen risico';
    },
    getProductPremiumInfo() {
      return { product: this.summary.productName || 'requestedSummary', premium: this.summary.totalNetPremium };
    },
    validationsOtherThanAuditExist() {
      return (Array.isArray(this.summary.validations))
          ? this.summary.validations.filter(val => val.level !== 'AUDIT' && val.level !== 'INFO').length > 0 : false;
    },
    errorsExist() {
      return (Array.isArray(this.summary.validations))
          ? this.summary.validations.some(val => val.level === 'ERROR') : false;
    }
  },
  watch: {
    policyId() {
      if (this.policyId) this.loadSummary();
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/styles/policy-card";

.card {
  border: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/case/PolicyCard.vue